import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { EmptyState, Skeleton } from '@/components/ui/misc'
import { getPrompts } from '@/lib/api/prompts/get-prompts'
import { apiErrorMessage } from '@/lib/api-client'

/** The system prompts as they are sent, one block per stage. Read-only. */
export function PromptsPage() {
  const { t } = useTranslation()
  const prompts = useQuery({ queryKey: ['prompts'], queryFn: getPrompts })

  return (
    <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl p-4">
        <div className="mb-3 flex items-baseline gap-2">
          <h2 className="text-sm font-semibold">{t('prompts.heading')}</h2>
          {prompts.data?.model && (
            <span className="text-[11px] text-muted-foreground">{prompts.data.model}</span>
          )}
        </div>

        {prompts.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : prompts.error ? (
          <EmptyState title={t('common.loadFailed', { message: apiErrorMessage(prompts.error) })} />
        ) : (
          <div className="space-y-4">
            {(prompts.data?.prompts ?? []).map((p) => (
              <section key={p.id}>
                <div className="mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t(`prompts.kind.${p.id}` as const)}
                  </span>
                  {Object.entries(p.params).map(([k, v]) => (
                    <span key={k} className="text-[10px] tabular-nums text-muted-foreground/80">
                      {k} = {String(v)}
                    </span>
                  ))}
                  <span className="ml-auto text-[10px] text-muted-foreground/70">
                    {t('common.characters', { count: p.prompt.length })}
                  </span>
                </div>
                <pre className="scrollbar-thin max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-2 font-mono text-[11px] leading-relaxed">
                  {p.prompt}
                </pre>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
